import { createContext, useEffect, useRef, useState } from "react";
import HeaderEstimate from "./components/header/headerEstimate";
import FormEstimateMarketPrice from "./components/forms/formMarketPrice/formEstimateMarketPrice";
import AboutObjectEstimate from "./components/aboutObject/aboutObjectEstimate";
import {
  getTransformedMinMaxMarketPrice,
  toReadablePrice,
} from "../../../lib/utils";
import {
  ISecondaryFacilityEstimateResponse,
  IPrimaryFacilityEstimateResponse,
} from "./interfaces";
import clsx from "clsx";
import PossibilitiesEstimate, {
  TPossibilitiesEstimate,
} from "./components/possibilities";

import style from "./styles.module.scss";

export interface ISecondaryFacilityEstimateResponseWithAddress
  extends ISecondaryFacilityEstimateResponse {
  address: string;
}
export interface IPrimaryFacilityEstimateResponseWithAddress
  extends IPrimaryFacilityEstimateResponse {
  address: string;
}
export interface IDataModal {
  apartmentNumber?: number;
  areaInSqM?: string;
  typeApartment?: string;
  facilityTypeId?: number;
  address?: string;
  buildingId?: number;
  streetId?: number;
  complexId?: number;
  estimatedPrice?: number;
}
export interface IDataFormEstimate {
  streetId?: number;
  complexId?: number;
  buildingId?: number;
  areaInSqM?: number;
  apartmentNumber?: number | null;
  facilityTypeId?: number | null;
}
interface IDataEstimateContext {
  dataModal: IDataModal | null;
  onSetDataModal: (data: IDataModal | null) => void;
}

export type TModeEstimate =
  | "tradeIn"
  | "quiz"
  | "default"
  | "selection"
  | "dealService"
  | "aboutFacilityForm";

interface IEstimate {
  mode?: TModeEstimate;
  modePossibilities?: TPossibilitiesEstimate;
  onDataFormEstimate?: (data: IDataFormEstimate) => void;
  onDataEstimatePrice?: (data: number) => void;
  dataForm?: IDataFormEstimate;
}

export const DataEstimateContext = createContext<IDataEstimateContext | null>(
  null
);

const Estimate = ({
  mode = "default",
  modePossibilities = "default",
  dataForm,
  onDataFormEstimate,
  onDataEstimatePrice,
}: IEstimate) => {
  const [data, setData] = useState<
    | ISecondaryFacilityEstimateResponseWithAddress
    | IPrimaryFacilityEstimateResponseWithAddress
    | null
  >(null);

  const handleSetData = (
    data:
      | ISecondaryFacilityEstimateResponseWithAddress
      | IPrimaryFacilityEstimateResponseWithAddress
  ) => {
    setData(data);
  };

  const handleResetData = () => {
    setData(null);
  };

  const [isSecondarySwitcher, setSwitcher] = useState(
    mode === "quiz" || mode === "selection" || mode === "tradeIn" ? true : true
  );
  const handleSwitch = (value: boolean) => {
    setSwitcher(value);
  };

  useEffect(() => {
    handleResetData();
  }, [isSecondarySwitcher]);

  const getYear = () => {
    if (data) {
      if ("commissioningYear" in data) {
        return data.commissioningYear;
      } else {
        if (data.commissioningDate) {
          return data.commissioningDate;
        } else {
          return data.commissioningQuarter;
        }
      }
    }
  };

  const getImages = () => {
    if (data && "plans" in data) {
      return data.plans.map((plan) => ({ src: plan.url, alt: plan.name }));
    }
  };

  const [dataModal, setDataModal] = useState<IDataModal | null>(null);
  const handleSetDataModal = (data: IDataModal | null) => {
    return setDataModal(data);
  };

  useEffect(() => {
    if (data?.averageMarketPrice) {
      if (onDataEstimatePrice) {
        onDataEstimatePrice(data?.averageMarketPrice);
      }
      setDataModal((prev) => ({
        ...prev,
        estimatedPrice: data.averageMarketPrice,
      }));
    }
  }, [data]);

  return (
    <section className={style.estimate}>
      <DataEstimateContext.Provider
        value={{
          dataModal,
          onSetDataModal: handleSetDataModal,
        }}
      >
        <div
          className={clsx(
            style.estimate__container,
            mode !== "default" && style.estimate__container_withoutMargin,
            mode !== "default" && style.estimate__container_withoutPadding
          )}
        >
          {mode === "default" && (
            <HeaderEstimate
              switcher={isSecondarySwitcher}
              onSwitch={handleSwitch}
            />
          )}
          <FormEstimateMarketPrice
            isSecondarySwitcher={isSecondarySwitcher}
            onCallBackData={handleSetData}
            resetData={handleResetData}
            mode={mode}
            onDataFormEstimate={onDataFormEstimate}
            dataForm={dataForm}
          />
          {data && (
            <div>
              <AboutObjectEstimate
                isSecondary={isSecondarySwitcher}
                maxFacilityPrice={getTransformedMinMaxMarketPrice(
                  String(data.maxFacilityPrice)
                )}
                minFacilityPrice={getTransformedMinMaxMarketPrice(
                  String(data.minFacilityPrice)
                )}
                marketPrice={toReadablePrice(data.averageMarketPrice)}
                floorCount={data.floorCount}
                wallMaterial={data.wallMaterial}
                commissioningYear={String(getYear()) + " г"}
                recommendations={data.recommendations}
                sameFacilityCount={data.sameFacilityCount}
                address={data.address}
                images={getImages()}
                mode={mode}
              />
              {mode === "default" && (
                <PossibilitiesEstimate
                  mode={modePossibilities}
                  averageMarketPrice={toReadablePrice(data.averageMarketPrice)}
                  mortgageInfo={data.mortgageInfo}
                  isSecondary={isSecondarySwitcher}
                />
              )}
            </div>
          )}
          <div className={style.estimate__disclaimer}>
            Расчет проводится автоматически на основе находящихся в продаже
            объектов.И может быть скорректирован сотрудником ООО "Реит". Не
            оферта.
          </div>
        </div>
      </DataEstimateContext.Provider>
    </section>
  );
};

export default Estimate;
